/**
 * EnterpriseContractsPage.tsx — Enterprise contracts list with create/edit actions
 *
 * Purpose:
 *   Displays a DataTable of enterprise contracts from GET /admin/enterprise/contracts.
 *   Provides a 'New Contract' button and per-row 'Edit' action that open the
 *   EnterpriseContractForm modal.
 *
 * @module packages/billing-admin/src/pages/EnterpriseContractsPage
 * @since 2026-06-03 (AT-50.03 — Revenue Dashboard & Enterprise Screens)
 */
import React, { useState } from 'react';
import DataTable, { type Column } from '../components/DataTable';
import EnterpriseContractForm from '../components/enterprise/EnterpriseContractForm';
import {
  deleteContract,
  useEnterpriseContracts,
  type EnterpriseContract,
} from '../hooks/useAdminApi50';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import SlidePanel from '../components/SlidePanel';

/**
 * EnterpriseContractsPage — contract list with modal create / edit.
 *
 * @since 2026-06-03 (AT-50.03)
 */
export default function EnterpriseContractsPage() {
  const { data, isLoading, error, refetch } = useEnterpriseContracts();
  const { token } = useAdminAuth();

  const [formState, setFormState] = useState<
    { open: false } | { open: true; contract?: EnterpriseContract }
  >({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<EnterpriseContract | null>(null);
  const [detailContract, setDetailContract] = useState<EnterpriseContract | null>(null);

  function openCreate() {
    setFormState({ open: true });
  }

  function openEdit(contract: EnterpriseContract) {
    setFormState({ open: true, contract });
  }

  function handleFormSuccess() {
    setFormState({ open: false });
    void refetch();
  }

  const columns: Column<EnterpriseContract>[] = [
    { key: 'customer_name', header: 'Customer', render: r => r.customer_name },
    { key: 'po_number', header: 'PO Number', render: r => r.po_number },
    {
      key: 'contract_value',
      header: 'Value',
      render: r => `$${r.contract_value.toLocaleString()}`,
    },
    { key: 'billing_interval', header: 'Interval', render: r => r.billing_interval },
    { key: 'start_date', header: 'Start Date', render: r => r.start_date },
    { key: 'end_date', header: 'End Date', render: r => r.end_date },
    { key: 'status', header: 'Status', render: r => r.status },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: r => (
        <div className="flex gap-2">
          <button
            className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            onClick={() => openEdit(r)}
          >
            Edit
          </button>
          <button
            className="px-3 py-1 text-xs border border-red-200 rounded-md text-red-600 hover:bg-red-50"
            onClick={() => setDeleteTarget(r)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout title="Contracts">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Commercial terms</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Enterprise contracts</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Manage negotiated enterprise agreements alongside the standard catalog so platform teams can administer higher-touch billing relationships.
              </p>
            </div>
            <button
              onClick={openCreate}
              className="rounded-full bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
            >
              New Contract
            </button>
          </div>
        </section>

        {error && <p className="text-red-500 text-sm">{error.message}</p>}

        <DataTable
          columns={columns}
          rows={data ?? []}
          rowKey={r => r.id}
          loading={isLoading}
          emptyMessage="No contracts found."
          onRowClick={(row) => setDetailContract(row)}
        />

        {formState.open && (
          <EnterpriseContractForm
            contract={formState.contract}
            onSuccess={handleFormSuccess}
            onClose={() => setFormState({ open: false })}
          />
        )}

        <ConfirmDialog
          open={deleteTarget !== null}
          title="Delete Contract"
          message={`Delete contract ${deleteTarget?.po_number || deleteTarget?.id || ''}? This will deactivate the agreement.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => {
            if (!deleteTarget || !token) {
              return;
            }
            void deleteContract(deleteTarget.id, token).then(() => {
              setDeleteTarget(null);
              void refetch();
            });
          }}
          onCancel={() => setDeleteTarget(null)}
        />

        {/* Contract detail slide-in */}
        <SlidePanel
          open={detailContract !== null}
          onClose={() => setDetailContract(null)}
          title={detailContract?.customer_name ?? 'Contract'}
          subtitle={detailContract?.po_number ?? undefined}
          size="md"
        >
          {detailContract && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-100 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Contract Value</p>
                  <p className="mt-1.5 text-lg font-semibold text-slate-900">${detailContract.contract_value.toLocaleString()}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Billing Interval</p>
                  <p className="mt-1.5 text-lg font-semibold text-slate-900 capitalize">{detailContract.billing_interval}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Start Date</p>
                  <p className="mt-1.5 text-sm text-slate-700">{detailContract.start_date}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">End Date</p>
                  <p className="mt-1.5 text-sm text-slate-700">{detailContract.end_date}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Status</p>
                  <p className="mt-1.5 text-sm font-medium text-slate-700 capitalize">{detailContract.status}</p>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Customer ID</p>
                  <p className="mt-1.5 font-mono text-xs text-slate-600">{detailContract.customer_id}</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setDetailContract(null); openEdit(detailContract); }}
                  className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800"
                >
                  Edit Contract
                </button>
                <button
                  onClick={() => { setDetailContract(null); setDeleteTarget(detailContract); }}
                  className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </SlidePanel>
      </div>
    </AdminLayout>
  );
}
